<template>
  <template v-if="!videoError">
    <video
      class="video-player"
      :key="videoSrc"
      ref="video"
      controls
      @timeupdate="updateTime"
    />
  </template>
  <NoVideo class="card" v-else>
    <template #additional-message>
      <slot name="video-error" />
    </template>
  </NoVideo>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from "vue";
import Hls from "hls.js";
import NoVideo from "../atoms/NoVideo.vue";

type TranscriptionVideoProps = {
  videoSrc: string;
  time: number;
};
const { videoSrc, time = 0 } = defineProps<TranscriptionVideoProps>();

const emit = defineEmits<{
  updateTime: [number];
}>();
const video = ref<HTMLVideoElement | null>(null);
const videoError = ref(false);

const updateTime = () => {
  if (video.value) {
    emit("updateTime", video.value.currentTime);
  }
};
watchEffect(() => {
  if (video.value) video.value.currentTime = time;
});

let errorsCount = 0;
const maxCount = 2;
onMounted(() => {
  if (video.value && videoSrc) {
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoSrc);
      hls.attachMedia(video.value);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (video.value) {
          video.value.muted = true;
          video.value.play();
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (errorsCount < maxCount) {
                errorsCount++;
                // Set a timeout to delay displaying the video error message by 5 seconds.
                setTimeout(() => {
                  hls.detachMedia();
                  hls.loadSource(videoSrc);
                  hls.attachMedia(video.value as HTMLVideoElement);
                  hls.startLoad();
                }, 2500);
              } else {
                hls.destroy();
                videoError.value = true;
              }
              break;
            default:
              hls.destroy();
              videoError.value = true;
              break;
          }
        }
      });
    } else if (video.value.canPlayType("application/vnd.apple.mpegurl")) {
      // For Safari (supports .m3u8 natively)
      video.value.src = videoSrc;
      video.value.addEventListener("loadedmetadata", () => {
        if (video.value) video.value.play();
      });
    } else {
      //console.error("Your browser does not support HLS playback.");
    }
  }
});
</script>
<style scoped>
.video-player {
  width: 100%;
  height: auto;
  display: flex;
  border-radius: 4px;
}
</style>
